import React from 'react';
import { Image } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { FormEditor, type FormEditorFormState } from './FormEditor';
import { MediaPreview, type MediaData } from './MediaTextPreview';
import { hasCropApplied } from '../shared/cropUtils';

export type FormSplitEditorFormState = FormEditorFormState & {
  media?: MediaData;
  mediaPosition?: 'left' | 'right';
  stackOnMobile?: boolean;
};

interface FormSplitEditorProps {
  formData: FormSplitEditorFormState;
  setFormData: (data: FormSplitEditorFormState) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string) => void;
  currentPageComponents?: Component[];
}

export const FormSplitEditor: React.FC<FormSplitEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData;

  const openMediaUpload = () => {
    onOpenMediaUpload(data.media?.type || 'image', 'media');
  };

  const openThumbnailUpload = () => {
    onOpenMediaUpload('image', 'thumbnail');
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Layout</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media position</label>
            <select
              value={data.mediaPosition === 'right' ? 'right' : 'left'}
              onChange={e =>
                setFormData({
                  ...data,
                  mediaPosition: e.target.value as 'left' | 'right',
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="left">Media left</option>
              <option value="right">Media right</option>
            </select>
          </div>
          <div className="flex items-center pt-8">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.stackOnMobile !== false}
                onChange={e => setFormData({ ...data, stackOnMobile: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Stack media below form on small screens</span>
            </label>
          </div>
        </div>
      </div>

      <FormSplitMediaSection
        data={data}
        setFormData={setFormData}
        openMediaUpload={openMediaUpload}
      />

      {data.media?.type === 'video' &&
        (!data.media?.sourceType || data.media?.sourceType === 'upload') && (
          <FormSplitThumbnailSection
            data={data}
            setFormData={setFormData}
            openThumbnailUpload={openThumbnailUpload}
          />
        )}

      {data.media?.type === 'video' &&
        (data.media?.sourceType === 'youtube' || data.media?.sourceType === 'vimeo') && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              {data.media?.sourceType === 'youtube' ? 'YouTube' : 'Vimeo'} videos use their own
              thumbnail. To use a custom thumbnail, upload the video file directly instead.
            </p>
          </div>
        )}

      <FormEditor
        formData={data as FormEditorFormState}
        setFormData={up => setFormData(up as FormSplitEditorFormState)}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        colorFieldsComponentType="form-split"
      />
    </div>
  );
};

interface FormSplitMediaSectionProps {
  data: FormSplitEditorFormState;
  setFormData: (data: FormSplitEditorFormState) => void;
  openMediaUpload: () => void;
}

const FormSplitMediaSection: React.FC<FormSplitMediaSectionProps> = ({
  data,
  setFormData,
  openMediaUpload,
}) => {
  const isVideo = data.media?.type === 'video';
  const isCropped = !isVideo && hasCropApplied(data.media?.crop);

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Media</h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media type</label>
            <select
              value={data.media?.type || 'image'}
              onChange={e => {
                const newType = e.target.value as 'image' | 'video';
                const updatedMedia = { ...data.media, type: newType };
                if (newType === 'video') {
                  delete (updatedMedia as { crop?: unknown }).crop;
                  if (updatedMedia.borderRadius === 'circle') {
                    updatedMedia.borderRadius = 'large';
                  }
                }
                setFormData({ ...data, media: updatedMedia });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Media content</label>
            <MediaPreview data={data} setFormData={setFormData} openMediaUpload={openMediaUpload} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Alt text / description
          </label>
          <input
            type="text"
            value={data.media?.alt || ''}
            onChange={e => setFormData({ ...data, media: { ...data.media, alt: e.target.value } })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Describe the media content"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {!isVideo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aspect ratio</label>
              <select
                value={isCropped ? 'crop' : data.media?.aspectRatio || 'auto'}
                onChange={e =>
                  setFormData({ ...data, media: { ...data.media, aspectRatio: e.target.value } })
                }
                disabled={isCropped}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 ${isCropped ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCropped ? (
                  <option value="crop">Set by image crop</option>
                ) : (
                  <>
                    <option value="auto">Auto</option>
                    <option value="16:9">16:9 (Widescreen)</option>
                    <option value="4:3">4:3 (Standard)</option>
                    <option value="1:1">1:1 (Square)</option>
                    <option value="3:2">3:2 (Photo)</option>
                    <option value="9:16">9:16 (Portrait)</option>
                  </>
                )}
              </select>
              {isCropped && (
                <p className="text-xs text-gray-500 mt-1">
                  Aspect ratio is controlled by the image crop. Edit or reset the crop to change it.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Border radius</label>
            <select
              value={
                isVideo && data.media?.borderRadius === 'circle'
                  ? 'large'
                  : data.media?.borderRadius || 'none'
              }
              onChange={e =>
                setFormData({ ...data, media: { ...data.media, borderRadius: e.target.value } })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="none">None</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
              {!isVideo && <option value="circle">Circle</option>}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

interface FormSplitThumbnailSectionProps {
  data: FormSplitEditorFormState;
  setFormData: (data: FormSplitEditorFormState) => void;
  openThumbnailUpload: () => void;
}

const FormSplitThumbnailSection: React.FC<FormSplitThumbnailSectionProps> = ({
  data,
  setFormData,
  openThumbnailUpload,
}) => {
  const handleRemoveThumbnail = () => {
    setFormData({
      ...data,
      media: {
        ...data.media,
        thumbnail: undefined,
      },
    });
  };

  const thumbnailUrl = data.media?.thumbnail?.cloudfrontUrl || data.media?.thumbnail?.url;

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Video thumbnail</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail image</label>
          <p className="text-xs text-gray-500 mb-3">Shown before the video plays</p>

          {thumbnailUrl ? (
            <div className="space-y-3">
              <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group inline-block">
                <img
                  src={thumbnailUrl}
                  alt={data.media?.thumbnail?.alt || 'Video thumbnail'}
                  className="w-32 h-24 object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={openThumbnailUpload}
                      className="px-2 py-1 bg-white text-gray-700 rounded shadow text-xs"
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveThumbnail}
                      className="px-2 py-1 bg-red-600 text-white rounded shadow text-xs"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
              onClick={openThumbnailUpload}
              onKeyDown={e => e.key === 'Enter' && openThumbnailUpload()}
              role="button"
              tabIndex={0}
            >
              <div className="flex flex-col items-center space-y-2">
                <div className="p-2 bg-gray-100 group-hover:bg-primary-100 rounded-full">
                  <Image size={24} className="text-gray-600 group-hover:text-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Upload thumbnail</p>
                  <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Alt text</label>
          <input
            type="text"
            value={data.media?.thumbnail?.alt || ''}
            onChange={e =>
              setFormData({
                ...data,
                media: {
                  ...data.media,
                  thumbnail: {
                    url: data.media?.thumbnail?.url || '',
                    ...data.media?.thumbnail,
                    alt: e.target.value,
                  },
                },
              })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Describe the thumbnail image"
          />
        </div>
      </div>
    </div>
  );
};
